import React, { FC, PropsWithChildren, ReactNode, useEffect } from 'react';
import { logger, LoggerTypes } from '../../utilities/logger/logger';
import { EventActions, EventLabels, ReceivedVariations, Variations } from './types';
import { useExperiments } from './useExperiments';
import { getExperiment, isValidVariation } from './utilities';

interface ExperimentProps {
  children?: ReactNode;
  exclude?: string | boolean;
  legacy?: boolean;
  id: string;
  variations: ReceivedVariations;
  trackingMigration?: boolean;
}

export const Experiment: FC<PropsWithChildren<ExperimentProps>> = (props) => {
  const { children, id: experimentId, variations, exclude, legacy, trackingMigration } = props;
  const { allocations, track, jimdoApp, debugMode } = useExperiments();
  if (!experimentId) {
    logger(LoggerTypes.ERROR, 'An experimentId is required and non was provided', jimdoApp, {
      experimentId,
      exclude,
    });
  }

  const currentExperiment = getExperiment(experimentId, allocations);
  const currentExperimentGroup = currentExperiment?.group;

  const controlComponent = (variations || []).reduce((acc: ReactNode, value) => {
    // checking for node because the component will be used in vanilla javascript and we do not type safety
    if (value && value.group === Variations.FLAG_CONTROL && value.hasOwnProperty('node')) {
      return value.node;
    }
    return acc;
  }, children);

  // Render control group if there is a manual exclusion or if the returned variation equals to "excluded"
  const renderControlOnExclusion =
    !!exclude || (currentExperimentGroup && currentExperimentGroup === Variations.FLAG_EXCLUDED);

  // If there is no variations, show controlComponent
  const showControlComponent = (!!variations && variations.length === 0) || !variations || !experimentId;

  // if all goes well – show variation
  const showVariation =
    !!currentExperimentGroup && !!variations && isValidVariation(variations, currentExperimentGroup);

  const experimentFlagNotFound = Boolean(!currentExperimentGroup && allocations.length > 0 && !!experimentId);

  useEffect(() => {
    if (renderControlOnExclusion) {
      if (debugMode) {
        logger(LoggerTypes.LOG, 'User excluded, showing control', jimdoApp, {
          experimentId,
          currentExperimentGroup,
          exclude,
        });
      }
      track({
        action: legacy ? EventActions.EXCLUDE : EventActions.ALLOCATION,
        label: legacy ? EventLabels.ABTEST : EventLabels.SUCCESS,
        payload: {
          auto: true,
          testId: experimentId,
          experimentId,
          testGroup: Variations.FLAG_EXCLUDED,
          experimentGroup: Variations.FLAG_EXCLUDED,
          reason: typeof exclude === 'string' ? exclude : undefined,
        },
      });
    } else if (showControlComponent) {
      if (debugMode) {
        logger(LoggerTypes.LOG, 'Showing control', jimdoApp, {
          experimentId,
          currentExperimentGroup,
          exclude,
        });
      }
      track({
        action: legacy ? EventActions.MISSING_VARIATION : EventActions.ERROR,
        label: legacy ? EventLabels.ABTEST : EventLabels.MISSING_VARIATION,
        payload: {
          auto: true,
          testId: experimentId,
          experimentId,
          testGroup: currentExperimentGroup,
          experimentGroup: currentExperimentGroup,
        },
      });
    } else if (showVariation) {
      /*
      * legacy  | trackingMigration | Result
           x    |       x           | ALLOCATION
           x    |       -           | ALLOCATION
           -    |       x           | SEEN
           -    |       -           | ALLOCATION
      */
      if (debugMode) {
        logger(LoggerTypes.LOG, `Showing variation: ${currentExperimentGroup.toString()}`, jimdoApp, {
          experimentId,
          currentExperimentGroup,
          exclude,
        });
      }
      let action = EventActions.ALLOCATION;
      if (trackingMigration && !legacy) {
        action = EventActions.SEEN;
      }
      track({
        action,
        label: legacy ? EventLabels.ABTEST : EventLabels.SUCCESS,
        payload: {
          auto: true,
          testId: experimentId,
          experimentId,
          testGroup: currentExperimentGroup,
          experimentGroup: currentExperimentGroup,
        },
      });
    } else if (!currentExperimentGroup) {
      logger(LoggerTypes.INFO, 'Waiting for allocations...', jimdoApp, {
        experimentId,
        currentExperimentGroup,
        exclude,
      });
    }
  }, [currentExperimentGroup]);

  if (experimentFlagNotFound) {
    logger(
      LoggerTypes.ERROR,
      `Provided experiment id "${experimentId}" cannot be found in allocations. The experiment flag is either archived or you provided an experiment id which does not exist on launchdarkly.`,
      jimdoApp,
      {
        experimentId,
      },
    );
    return <>{controlComponent}</>;
  }

  if (renderControlOnExclusion) {
    return <>{controlComponent}</>;
  }

  if (showControlComponent) {
    return <>{controlComponent}</>;
  }

  if (showVariation) {
    const experimentVariation = variations.filter((variation) => variation.group === currentExperimentGroup)[0];
    return <>{experimentVariation?.node ?? controlComponent}</>;
  } else if (currentExperimentGroup && !isValidVariation(variations, currentExperimentGroup)) {
    logger(
      LoggerTypes.ERROR,
      'User allocation does not match with (control|target|target-a|target-b|excluded)',
      jimdoApp,
      {
        experimentId,
        currentExperimentGroup,
        exclude,
      },
    );
    track({
      action: legacy ? EventActions.INVALID_EXPERIMENT_GROUP : EventActions.ERROR,
      label: legacy ? EventLabels.ABTEST : EventLabels.INVALID_EXPERIMENT_GROUP,
      payload: {
        auto: true,
        testId: experimentId,
        experimentId,
        testGroup: currentExperimentGroup,
        experimentGroup: currentExperimentGroup,
      },
    });
    return <>{controlComponent}</>;
  } else if (!currentExperimentGroup) {
    return null;
  }

  logger(LoggerTypes.ERROR, 'Unexpected behaviour. Use case not defined', jimdoApp, {
    experimentId,
    currentExperimentGroup,
    exclude,
  });
  return null;
};
